import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, X, FileText } from "lucide-react";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Modal,
  TextField,
  CircularProgress,
  Divider
} from "@mui/material";

const DashboardBlog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    image: null,
    document: null,
  });

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BLOGS_API_URL}`);
      const data = await response.json();
      if (data.success) {
        setBlogs(data.blogs);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

const handleSubmit = async () => {
  if (!formData.title) {
    alert("Please enter a title");
    return;
  }

  if (!editingBlog && (!formData.image || !formData.document)) {
    alert("Please select both image and document");
    return;
  }

  const fd = new FormData();
  fd.append("title", formData.title);

  if (formData.image) fd.append("image", formData.image);
  if (formData.document) fd.append("document", formData.document);

const url = editingBlog
  ? `${process.env.REACT_APP_BLOGS_API_URL}?action=update&id=${editingBlog.id}`
  : `${process.env.REACT_APP_BLOGS_API_URL}?action=create`;


  try {
    const response = await fetch(url, {
      method: "POST", // ✅ ALWAYS POST (PHP SAFE)
      body: fd,
    });

    const data = await response.json();

    if (data.success) {
      alert(data.message);
      resetForm();
      setShowForm(false);
      fetchBlogs();
    } else {
      alert(data.message || "Failed to save blog");
    }
  } catch (err) {
    console.error(err);
    alert("Server error while saving blog");
  }
};


const truncateText = (text, maxLength) => {
  if (!text) return "";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({ title: blog.title, image: null, document: null });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BLOGS_API_URL}?id=${id}`, {
  method: "DELETE"
});

      const data = await response.json();

      if (data.success) {
        alert(data.message);
        fetchBlogs();
      } else {
        alert(data.message || "Error deleting blog");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Error deleting blog");
    }
  };

  const resetForm = () => {
    setFormData({ title: "", image: null, document: null });
    setEditingBlog(null);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Typography variant="h3" fontWeight="bold" color="primary" gutterBottom>
        Blog Admin Panel
      </Typography>

      {/* <Typography variant="subtitle1" color="text.secondary" mb={4}>
        Manage your blog posts
      </Typography> */}

      <Button
        variant="contained"
        startIcon={<Plus size={20} />}
        onClick={() => setShowForm(true)}
        sx={{ mb: 4, py: 1.5, px: 4 }}
      >
        Add New Blog
      </Button>

<Modal
  open={showForm}
  onClose={() => {
    resetForm();
    setShowForm(false);
  }}
>
        <Box
          sx={{
            width: "600px",
            bgcolor: "white",
            p: 4,
            borderRadius: 3,
            mx: "auto",
            mt: 8,
          }}
        >
          <Box display="flex" justifyContent="space-between" mb={3}>
            <Typography variant="h5" fontWeight={600}>
              {editingBlog ? "Edit Blog" : "Add New Blog"}
            </Typography>
            <Button onClick={() => setShowForm(false)}>
              <X size={28} />
            </Button>
          </Box>

          <TextField
            fullWidth
            label="Blog Title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 1 }} />

          <Button variant="outlined" component="label" fullWidth sx={{ mb: 2 }}>
            Upload Image *
            <input type="file" accept="image/*" hidden
              onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
            />
          </Button>

          <Button variant="outlined" component="label" fullWidth sx={{ mb: 3 }}>
            Upload Document (PDF/DOCX) *
            <input
              type="file"
              accept=".doc,.docx,.pdf"
              hidden
              onChange={(e) =>
                setFormData({ ...formData, document: e.target.files[0] })
              }
            />
          </Button>

          <Button variant="contained" fullWidth sx={{ py: 1.5 }} onClick={handleSubmit}>
            {editingBlog ? "Update Blog" : "Add Blog"}
          </Button>
        </Box>
      </Modal>

      {loading ? (
        <Box textAlign="center" my={10}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
  {blogs.map((blog) => (
    <Grid item xs={12} sm={6} md={4} key={blog.id}>
      <Card sx={{ p: 2, borderRadius: 3 }}>
        <CardMedia
          component="img"
          image={`${process.env.REACT_APP_BASE_URL}/uploads/${blog.image}`}
          alt={blog.title}
          sx={{ height: 180, borderRadius: 2, objectFit: "cover" }}
        />
        <CardContent>
          <Typography variant="h6" fontWeight="bold">
            {blog.title}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {new Date(blog.created_at).toLocaleDateString()}
          </Typography>

          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <FileText size={18} />
            <Typography variant="body2" color="text.secondary">
              {truncateText(blog.document, 150)}
            </Typography>
          </Box>

          <Box display="flex" gap={2} mt={2}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Edit2 size={18} />}
              onClick={() => handleEdit(blog)}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Trash2 size={18} />}
              onClick={() => handleDelete(blog.id)}
            >
              Delete
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  ))}
</Grid>

      )}
    </Container>
  );
};

export default DashboardBlog;
